/**
 * 按agent分组数据的工具函数
 * 1. agent为report的type为text的需要直接将data拼接
 * 2. 除agent为report之外的，type为reasoning的需要通过data中id相同做拼接，合并后仅仅保留一项
 * 3. 除agent为report之外的，toolcall通过toolcallId相同合并为一个obj，合并后仅仅保留一项
 * 对于toolcall类型，只保留toolCallName为web-search的数据
 */

// 原始数据项类型

export interface OriginalDataItem {
  agent: string;
  type: string;
  data: any;
}

// 处理后的数据项类型
export interface ProcessedDataItem {
  type: string;
  data: any;
  hidden?: boolean; // 可选的hidden标记
}

// 中间数据结构：按agent分类后的格式
export interface AgentChunk {
  agent: string;
  chunks: ProcessedDataItem[];
}

// 最终分组结果类型
export type GroupedData = Record<string, ProcessedDataItem[]>;

/**
 * 默认允许展示的 toolName 集合（在多处共享，避免重复维护）
 */
export const ALLOWED_TOOL_NAMES = [
  "web-search",
  "read_file",
  "write_file",
  "edit_file",
  "execute_command",
  "batch",
  "delete_file",
] as const;

/**
 * 过滤toolcall数据的配置接口
 */
export interface ToolcallFilterConfig {
  /** 要保留的toolName值（向后兼容） */
  allowedToolName?: string;
  /** 要保留的toolName集合（推荐） */
  allowedToolNames?: string[];
}

/**
 * 过滤toolcall数据，根据配置决定是否保留
 * @param item 原始数据项
 * @param config 过滤配置
 * @returns 是否应该保留该数据项
 */
function shouldKeepToolcallData(
  item: OriginalDataItem,
  config: ToolcallFilterConfig,
): boolean {
  if (item.type !== "toolcall") {
    return true; // 非toolcall类型直接保留
  }

  const toolName = item.data?.toolName;
  const allowedToolNames =
    config.allowedToolNames && config.allowedToolNames.length > 0
      ? config.allowedToolNames
      : config.allowedToolName
        ? [config.allowedToolName]
        : [];
  const shouldKeep = allowedToolNames.includes(toolName);

  // 添加调试信息

  return shouldKeep;
}

/**
 * 第一步：按agent分类数据，形成中间数据结构
 * @param dataArray 原始数据数组
 * @returns 按agent分类的数据数组
 */
export function classifyByAgent(dataArray: OriginalDataItem[]): AgentChunk[] {
  const agentMap = new Map<string, ProcessedDataItem[]>();

  dataArray.forEach((item) => {
    const { agent, type, data } = item;

    // 初始化agent分组
    if (!agentMap.has(agent)) {
      agentMap.set(agent, []);
    }

    // 添加数据项
    agentMap.get(agent)!.push({
      ...item,
      type,
      data,
    });
  });

  // 转换为AgentChunk数组格式
  return Array.from(agentMap.entries()).map(([agent, chunks]) => ({
    agent,
    chunks,
  }));
}

/**
 * 处理report agent的数据
 * text类型直接拼接，其他类型直接添加
 * 保持type的发现顺序
 * 当检测到hidden标记时，截断在该位置前，停止后续text拼接
 * @param chunks 数据块
 * @returns 处理后的数据数组
 */
function processReportAgentData(
  chunks: ProcessedDataItem[],
): ProcessedDataItem[] {
  // 找到第一个hidden标记的位置，只处理该位置之前的数据
  const hiddenIndex = chunks.findIndex((item) => item?.hidden === true);
  const hasHidden = hiddenIndex !== -1;

  if (hasHidden) {
  }

  // 只处理hidden之前的chunks，隔离visible内容
  // const visibleChunks = hasHidden ? chunks.slice(0, hiddenIndex) : chunks;
  const result: ProcessedDataItem[] = [];
  const textItems: ProcessedDataItem[] = [];
  const otherItems: ProcessedDataItem[] = [];
  const typeOrder: string[] = [];
  const seenTypes = new Set<string>();

  // 处理可见的chunks，记录type的发现顺序
  chunks.forEach((item) => {
    if (!seenTypes.has(item.type)) {
      typeOrder.push(item.type);
      seenTypes.add(item.type);
    }

    if (item.type === "text") {
      textItems.push(item);
    } else {
      otherItems.push(item);
    }
  });

  // 按照type的发现顺序处理数据
  typeOrder.forEach((type) => {
    if (type === "text" && textItems.length > 0) {
      // text类型：直接拼接所有data
      const concatenatedText = textItems
        .map((item) => item.data || "")
        .join("");

      result.push({
        type: "text",
        data: concatenatedText,
      });
    } else if (type !== "text") {
      // 其他类型：直接添加原始项
      const itemsOfType = otherItems.filter((item) => item.type === type);
      itemsOfType.forEach((item) => {
        result.push({
          type: item.type,
          data: item.data,
        });
      });
    }
  });

  // 如果检测到hidden，添加标记到result中，后续内容不再拼接
  if (hasHidden) {
    result.push({
      type: "__hidden__",
      data: { hidden: true },
    });
  }

  return result;
}

/**
 * 合并处理器接口
 */
interface MergeHandler {
  /** 要处理的类型名称 */
  type: string;
  /** 用于查找现有项的字段名 */
  idField: string;
  /** 合并逻辑函数 */
  merge: (existing: any, newData: any) => any;
}

/**
 * 相邻合并处理器接口
 * 只有当 result 数组末尾元素与当前元素同类型时才合并，否则创建新组。
 */
interface AdjacentMergeHandler {
  type: string;
  /** 将原始 data 转换为合并后的初始格式 */
  init: (data: any) => any;
  /** 将新 data 追加到已有合并数据中 */
  merge: (existing: any, newData: any) => any;
}

/**
 * 通用的数据处理函数
 * 按原始顺序处理数据，支持自定义类型的合并逻辑
 * @param chunks 数据块
 * @param filterConfig 过滤配置
 * @param mergeHandlers 自定义合并处理器数组
 * @param debugPrefix 调试日志前缀
 * @param adjacentMergeHandlers 相邻合并处理器数组（连续同类型才合并，断开则新建组）
 * @returns 处理后的数据数组
 */
function processAgentDataWithMerge(
  chunks: ProcessedDataItem[],
  filterConfig?: ToolcallFilterConfig,
  mergeHandlers: MergeHandler[] = [],
  _debugPrefix: string = "",
  adjacentMergeHandlers: AdjacentMergeHandler[] = [],
): ProcessedDataItem[] {
  const result: ProcessedDataItem[] = [];

  // 按原始顺序处理每个项目，保持位置顺序
  chunks.forEach((item) => {
    // 如果是toolcall类型且有过滤配置，先进行过滤
    if (item.type === "toolcall" && filterConfig) {
      const shouldKeep = shouldKeepToolcallData(
        { agent: "", type: item.type, data: item.data } as OriginalDataItem,
        filterConfig,
      );
      if (!shouldKeep) {
        return;
      }
    }

    // 检查是否有相邻合并处理器
    const adjacentHandler = adjacentMergeHandlers.find(
      (h) => h.type === item.type,
    );

    if (adjacentHandler) {
      const last = result[result.length - 1];
      if (last && last.type === adjacentHandler.type) {
        last.data = adjacentHandler.merge(last.data, item.data);
      } else {
        result.push({
          type: item.type,
          data: adjacentHandler.init(item.data),
        });
      }
      return;
    }

    // 查找是否有自定义的合并处理器
    const handler = mergeHandlers.find((h) => h.type === item.type);

    if (handler) {
      const existingIndex = result.findIndex(
        (r) =>
          r.type === handler.type &&
          r.data?.[handler.idField] === item.data?.[handler.idField],
      );

      if (existingIndex >= 0) {
        result[existingIndex].data = handler.merge(
          result[existingIndex].data,
          item.data,
        );
      } else {
        result.push({
          type: item.type,
          data: item.data,
        });
      }
    } else {
      result.push({
        type: item.type,
        data: item.data,
      });
    }
  });

  return result;
}

/**
 * 处理非report agent的数据
 * reasoning按id合并，toolcall按toolCallId合并，其他类型直接添加
 * 保持原始数据中的实际位置顺序
 * @param chunks 数据块
 * @param filterConfig 过滤配置
 * @returns 处理后的数据数组
 */
function processNonReportAgentData(
  chunks: ProcessedDataItem[] = [],
  filterConfig?: ToolcallFilterConfig,
): ProcessedDataItem[] {
  return processAgentDataWithMerge(
    chunks,
    filterConfig,
    [
      // reasoning类型：拼接text字段
      {
        type: "reasoning",
        idField: "id",
        merge: (existing, newData) => ({
          ...existing,
          text: existing.text + (newData?.text || ""),
        }),
      },
      // toolcall类型：对象合并
      {
        type: "toolcall",
        idField: "toolCallId",
        merge: (existing, newData) => ({
          ...existing,
          ...newData,
        }),
      },
    ],
    "",
    [
      // executing类型：相邻合并，连续的 executing 事件归入同一组
      {
        type: "executing",
        init: (data) => ({ lines: [data?.text || ""] }),
        merge: (existing, newData) => ({
          lines: [...(existing?.lines || []), newData?.text || ""],
        }),
      },
    ],
  );
}

/**
 * 处理report agent的数据分组
 * text类型直接拼接data，其他类型直接返回原始项
 * @param dataArray 原始数据数组
 * @returns report agent的分组数据
 */
export function groupReportAgent(dataArray: OriginalDataItem[]): GroupedData {
  // 第一步：按agent分类
  const agentChunks = classifyByAgent(dataArray);

  // 过滤出report agent的数据
  const reportChunk = agentChunks.find((chunk) => chunk.agent === "report");

  if (!reportChunk) {
    return {};
  }

  // 处理report agent数据
  const result: GroupedData = {};
  result["report"] = processReportAgentData(reportChunk.chunks);

  return result;
}

/**
 * 处理非report agent的数据分组（从agentChunks开始）
 * reasoning按id合并，toolcall按toolCallId合并，其他类型直接添加
 * @param agentChunks 按agent分类的数据数组
 * @param filterConfig 过滤配置
 * @returns 非report agent的分组数据
 */
export function processNonReportAgentChunks(
  agentChunks: AgentChunk[],
  filterConfig?: ToolcallFilterConfig,
): GroupedData {
  // 过滤出非report agent的数据
  const nonReportChunks = agentChunks.filter(
    (chunk) => chunk.agent !== "report",
  );

  if (nonReportChunks.length === 0) {
    return {};
  }

  // 处理非report agent数据
  const result: GroupedData = {};
  nonReportChunks.forEach(({ agent, chunks }) => {
    result[agent] = processNonReportAgentData(chunks, filterConfig);
  });

  return result;
}

/**
 * 处理非report agent的数据分组
 * reasoning按id合并，toolcall按toolCallId合并，其他类型直接添加
 * @param dataArray 原始数据数组
 * @returns 非report agent的分组数据
 */
export function groupNonReportAgent(
  dataArray: OriginalDataItem[],
): GroupedData {
  // 第一步：按agent分类
  const agentChunks = classifyByAgent(dataArray);

  // 使用新的方法处理非report agent数据，传递过滤配置
  return processNonReportAgentChunks(agentChunks, {
    allowedToolNames: [...ALLOWED_TOOL_NAMES],
  });
}

/**
 * 处理deepsearch agent的数据
 * summary类型按id合并，toolcall按toolCallId合并，其他类型直接添加
 * 保持原始数据的位置顺序，合并后的数据出现在第一次发现该id/toolCallId的位置
 * @param chunks 数据块
 * @param filterConfig 过滤配置
 * @returns 处理后的数据数组
 */
function processDeepsearchAgentData(
  chunks: ProcessedDataItem[],
  filterConfig?: ToolcallFilterConfig,
): ProcessedDataItem[] {
  return processAgentDataWithMerge(
    chunks,
    filterConfig,
    [
      // summary类型：对象合并
      {
        type: "summary",
        idField: "id",
        merge: (existing, newData) => ({
          ...existing,
          ...newData,
        }),
      },
      // toolcall类型：对象合并
      {
        type: "toolcall",
        idField: "toolCallId",
        merge: (existing, newData) => ({
          ...existing,
          ...newData,
        }),
      },
    ],
    "deepsearch ",
  );
}

/**
 * 处理deepsearch agent的数据分组（从agentChunks开始）
 * summary类型按id合并，toolcall按toolCallId合并，其他类型直接添加
 * @param agentChunks 按agent分类的数据数组
 * @param filterConfig 过滤配置
 * @returns deepsearch agent的分组数据
 */
export function processDeepsearchAgentChunks(
  agentChunks: AgentChunk[],
  filterConfig?: ToolcallFilterConfig,
): GroupedData {
  // 过滤出deepsearch agent的数据
  const deepsearchChunk = agentChunks.find(
    (chunk) => chunk.agent === "deepsearch",
  );

  if (!deepsearchChunk) {
    return {};
  }

  // 处理deepsearch agent数据，传递过滤配置
  const result: GroupedData = {};
  result["deepsearch"] = processDeepsearchAgentData(
    deepsearchChunk.chunks,
    filterConfig,
  );

  return result;
}

/**
 * 按deepsearch分组数据，根据类型采用不同的处理策略
 * 1. summary类型通过data中id相同做拼接，合并后保留所有不同id的合并结果
 * 2. toolcall类型通过toolCallId相同合并为一个obj，每个toolCallId保留一个合并后的对象
 * 3. 其他类型直接添加
 * 对于toolcall类型，只保留toolName为TavilySearchResults的数据
 * @param dataArray 原始数据数组
 * @returns deepsearch的分组数据
 */
export function groupByDeepSearch(dataArray: OriginalDataItem[]): GroupedData {
  // 第一步：按agent分类
  const agentChunks = classifyByAgent(dataArray);

  // 使用新的方法处理deepsearch agent数据，传递过滤配置
  return processDeepsearchAgentChunks(agentChunks, {
    allowedToolName: "TavilySearchResults",
  });
}

/**
 * 获取格式化后的agent数组中每个类型对应的data
 * @param agentData 格式化后的agent数组 (ProcessedDataItem[])
 * @returns 以type为key，data为value的对象
 */
export function getAgentDataByType(
  agentData: ProcessedDataItem[],
): Record<string, any> {
  if (!agentData || !Array.isArray(agentData)) {
    return {};
  }

  const result: Record<string, any> = {};

  agentData.forEach((item) => {
    const { type, data } = item;
    if (type && data !== undefined) {
      result[type] = data;
    }
  });

  return result;
}
